// ============================================================
// QuickWinsPage
// ============================================================
import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Table, Button, Tag, Space, Card, Typography, message,
  Modal, InputNumber, Tooltip, Badge, Progress, Input, Select,
} from "antd";
import { PlusOutlined, CheckOutlined, ThunderboltOutlined, SearchOutlined } from "@ant-design/icons";
import { quickWinsApi } from "../api/client";
import type { QuickWin } from "../types";
import dayjs from "dayjs";

const { Title, Text } = Typography;
const { Option } = Select;

const ESTADO_COLORS: Record<string, string> = {
  "En curso": "processing", Entregado: "success", "En espera": "warning", Descartado: "default",
};

export function QuickWinsPage() {
  const [data, setData] = useState<QuickWin[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [estadoFiltro, setEstadoFiltro] = useState<string | undefined>();
  const [cerrarModal, setCerrarModal] = useState<{ open: boolean; qwId: string; qwCode: string }>({
    open: false, qwId: "", qwCode: "",
  });
  const [esfuerzo, setEsfuerzo] = useState<number>(1);
  const [page, setPage] = useState(1);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const res = await quickWinsApi.listar({ page, page_size: 20, estado: estadoFiltro });
      setData(res.data.items);
      setTotal(res.data.total);
    } catch { message.error("Error cargando quick wins"); }
    finally { setLoading(false); }
  }, [page, estadoFiltro]);

  useEffect(() => { cargar(); }, [cargar]);

  const handleCerrar = async () => {
    try {
      await quickWinsApi.cerrar(cerrarModal.qwId, esfuerzo);
      message.success(`${cerrarModal.qwCode} marcado como Entregado`);
      setCerrarModal({ open: false, qwId: "", qwCode: "" });
      cargar();
    } catch { message.error("Error al cerrar"); }
  };

  const filtered = data.filter((q) =>
    !search || q.qw_id.toLowerCase().includes(search.toLowerCase()) ||
    q.titulo.toLowerCase().includes(search.toLowerCase())
  );

  const columns = [
    { title: "ID", dataIndex: "qw_id", width: 90, render: (v: string) => <Text code style={{ fontSize: 12 }}>{v}</Text> },
    { title: "Título", dataIndex: "titulo", ellipsis: true },
    { title: "Tipo", dataIndex: "tipo_solucion", width: 140, render: (v: string) => <Tag>{v}</Tag> },
    { title: "Área", dataIndex: "area_solicitante", width: 160, ellipsis: true },
    {
      title: "Estado", dataIndex: "estado", width: 110,
      render: (v: string) => <Badge status={ESTADO_COLORS[v] as any} text={v} />,
    },
    {
      title: "Replicable", dataIndex: "replicable", width: 90,
      render: (v: boolean) => v ? <Tag color="green">Sí</Tag> : null,
    },
    {
      title: "Entrega", dataIndex: "fecha_entrega", width: 100,
      render: (v: string) => v ? dayjs(v).format("DD/MM/YYYY") : <Text type="secondary">—</Text>,
    },
    {
      title: "", width: 90,
      render: (_: unknown, r: QuickWin) =>
        r.estado !== "Entregado" && r.estado !== "Descartado" ? (
          <Tooltip title="Marcar como entregado">
            <Button size="small" icon={<CheckOutlined />} type="link"
              style={{ color: "#52c41a" }}
              onClick={() => setCerrarModal({ open: true, qwId: r.id, qwCode: r.qw_id })} />
          </Tooltip>
        ) : null,
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <Space style={{ justifyContent: "space-between", width: "100%" }}>
          <div>
            <Title level={4} style={{ margin: 0, color: "#1a1f5e" }}>Quick Wins IABP</Title>
            <Text type="secondary" style={{ fontSize: 13 }}>Mejoras rápidas de bajo esfuerzo · Total: {total}</Text>
          </div>
          <Button type="primary" icon={<PlusOutlined />} style={{ background: "#1a1f5e" }}>
            Nuevo Quick Win
          </Button>
        </Space>
      </div>
      <Card size="small" style={{ marginBottom: 16 }}>
        <Space>
          <Input prefix={<SearchOutlined />} placeholder="Buscar..." value={search}
            onChange={(e) => setSearch(e.target.value)} allowClear style={{ width: 260 }} />
          <Select style={{ width: 150 }} placeholder="Estado" allowClear
            value={estadoFiltro} onChange={(v) => { setEstadoFiltro(v); setPage(1); }}>
            {["En curso","Entregado","En espera","Descartado"].map((e) => <Option key={e} value={e}>{e}</Option>)}
          </Select>
        </Space>
      </Card>
      <Card size="small">
        <Table dataSource={filtered} columns={columns} rowKey="id" loading={loading} size="small"
          pagination={{ current: page, pageSize: 20, total, onChange: setPage,
            showTotal: (t) => `${t} quick wins` }} />
      </Card>
      <Modal
        title={`Cerrar ${cerrarModal.qwCode}`}
        open={cerrarModal.open}
        onOk={handleCerrar}
        onCancel={() => setCerrarModal({ open: false, qwId: "", qwCode: "" })}
        okText="Confirmar entrega"
        okButtonProps={{ style: { background: "#1a1f5e" } }}
      >
        <p>Introduce el esfuerzo real invertido para registrar el cierre:</p>
        <InputNumber min={0.5} max={30} step={0.5} value={esfuerzo}
          onChange={(v) => setEsfuerzo(v ?? 1)} suffix="días" style={{ width: "100%" }} />
      </Modal>
    </div>
  );
}

export default QuickWinsPage;
